import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Waves, Droplets, Gauge, Thermometer, CheckCircle2, AlertCircle,
  Info, RotateCcw, Microscope, TrendingUp, BarChart3, Wind,
  ChevronDown, ChevronUp, Plus, Minus, X, FileText, Printer,
  Layers, Settings, FlaskConical, ArrowDown, ArrowRight, Zap
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer, Legend
} from 'recharts';

/* ─── Cascade Aerator Specification Page ──────────────────
   Multi-step aeration tower — Twort, Ratnayaka & Brandt
   Water Supply 5th Edition formula
   ────────────────────────────────────────────────────── */

// ─── Types ──────────────────────────────────────────────

interface CascadeStep {
  h: number; // fall height (m)
}

interface CascadeParams {
  // Inlet conditions
  flowRate: number;        // m³/h
  inletDO: number;         // mg/L (Cu₀)
  temperature: number;     // °C
  waterQualityA: number;   // 0.85 | 1.0 | 1.25
  weirTypeB: number;       // 1.0 | 1.3
  // Steps
  steps: CascadeStep[];
  // Weir
  weirLength: number;      // m (b)
  weirHeight: number;      // m (p1, height above floor)
  // Tower geometry
  towerDiameter: number;   // m
  towerHeight: number;     // m (total)
  // Ventilation
  exhaustO2Pct: number;    // % (outlet O2)
  // Target DO for validation
  targetDO: number;        // mg/L
}

interface StepResult {
  stepNum: number;
  h: number;
  r: number;
  cuUpstream: number;
  cdDownstream: number;
  doGain: number;
  pctSaturation: number;
}

interface CascadeResults {
  cs: number;              // DO saturation (mg/L)
  steps: StepResult[];
  finalDO: number;         // mg/L
  finalPctSat: number;     // %
  totalDOAbsorption: number; // mg/L gain
  doAbsorptionRate: number;  // kg/h
  weirLoadingRate: number;   // L/s/m
  headOverWeir: number;      // h1 (m)
  airRequirement: number;    // Nm³/h
  airChanges: number;        // per hour
  towerVolume: number;       // m³
  rTotal: number;            // product of all r values
  // Validation flags
  vWeirLoading: boolean;
  vFinalDO: boolean;
  vStepHeightMax: boolean[];
  vStepHeightMin: boolean[];
  vAirChanges: boolean;
  vStepR: boolean[];
}

// ─── Default parameters (matching Excel spreadsheet) ─────

const DEFAULT_STEPS: CascadeStep[] = [
  { h: 0.56 },
  { h: 0.40 },
  { h: 0.40 },
  { h: 0.40 },
  { h: 0.40 },
  { h: 2.863 },
];

const DEFAULT_PARAMS: CascadeParams = {
  flowRate: 574,
  inletDO: 0,
  temperature: 9,
  waterQualityA: 1.0,
  weirTypeB: 1.0,
  steps: DEFAULT_STEPS,
  weirLength: 6,
  weirHeight: 0.4,
  towerDiameter: 3,
  towerHeight: 5.674,
  exhaustO2Pct: 19,
  targetDO: 8.0,
};

// ─── Core Calculations ───────────────────────────────────

function calcDOSaturation(T: number): number {
  // Twort-Ratnayaka-Brandt polynomial fit
  const primary = 14.621 - 0.39363 * T + 0.007714 * T * T - 0.0000646 * T * T * T;
  // Fallback Benson approximation
  const fallback = 468 / (31.6 + T);
  // Use primary if reasonable, else fallback
  return primary > 0 && primary < 20 ? primary : fallback;
}

function calcDeficitRatio(a: number, b: number, h: number, T: number): number {
  // Twort-Ratnayaka-Brandt (Water Supply 5th Ed., p390-391)
  // r = 1 + 0.38 × a × b × h × (1 − 0.11·h) × (1 + 0.046·T)
  return 1 + 0.38 * a * b * h * (1 - 0.11 * h) * (1 + 0.046 * T);
}

function requiredFallHeight(r: number, a: number, b: number, T: number): number {
  // Rearranging for h from: r-1 = 0.38 × a × b × h × (1-0.11h) × (1+0.046T)
  // Simplified (ignoring 0.11h quadratic): h ≈ (r-1) / (0.38 × a × b × (1+0.046T))
  // Full quadratic: 0.38*a*b*(1+0.046T) × (h - 0.11h²) = r-1
  // Let K = 0.38*a*b*(1+0.046T), then K*h - 0.11K*h² = r-1
  // 0.11K*h² - K*h + (r-1) = 0
  const K = 0.38 * a * b * (1 + 0.046 * T);
  const A_coef = -0.11 * K;
  const B_coef = K;
  const C_coef = -(r - 1);
  const disc = B_coef * B_coef - 4 * A_coef * C_coef;
  if (disc < 0) return (r - 1) / K; // fallback linear
  const h1 = (-B_coef + Math.sqrt(disc)) / (2 * A_coef);
  const h2 = (-B_coef - Math.sqrt(disc)) / (2 * A_coef);
  // Take positive root in practical range
  const candidates = [h1, h2].filter(h => h > 0 && h < 5);
  return candidates.length > 0 ? Math.min(...candidates) : (r - 1) / K;
}

function solveWeirHead(Q_m3h: number, b: number, p1: number): number {
  // Sharp-crested weir: Q = Ce × (2/3) × √(2g) × be × he^1.5
  // Ce = K1 + K2 × (h1/p1),  K1=0.602, K2=0.075
  // be = b + Kb, Kb = -0.0009 m
  // he = h1 + 0.001 m
  const K1 = 0.602, K2 = 0.075, Kb = -0.0009;
  const g = 9.81;
  const Q_m3s = Q_m3h / 3600;
  const be = b + Kb;
  let h1 = 0.1; // initial guess (m)
  for (let iter = 0; iter < 50; iter++) {
    const Ce = K1 + K2 * (h1 / p1);
    const he = h1 + 0.001;
    const Q_calc = Ce * (2 / 3) * Math.sqrt(2 * g) * be * Math.pow(he, 1.5);
    const err = Q_calc - Q_m3s;
    if (Math.abs(err) < 1e-8) break;
    // Newton step: dQ/dh1 ≈ Ce*(2/3)*√(2g)*be*1.5*he^0.5 + (K2/p1)*(2/3)*√(2g)*be*he^1.5
    const dQdh = (K2 / p1) * (2 / 3) * Math.sqrt(2 * g) * be * Math.pow(he, 1.5)
                 + Ce * (2 / 3) * Math.sqrt(2 * g) * be * 1.5 * Math.pow(he, 0.5);
    h1 = Math.max(0.001, h1 - err / Math.max(dQdh, 1e-10));
  }
  return h1;
}

export function calcCascade(p: CascadeParams): CascadeResults {
  const T = p.temperature;
  const a = p.waterQualityA;
  const b = p.weirTypeB;
  const cs = calcDOSaturation(T);

  // Per-step DO calculations
  const stepResults: StepResult[] = [];
  let cuCurrent = p.inletDO; // Cu₀

  for (let i = 0; i < p.steps.length; i++) {
    const h = p.steps[i].h;
    const r = calcDeficitRatio(a, b, h, T);
    const cdDownstream = cs - (cs - cuCurrent) / r;
    const doGain = cdDownstream - cuCurrent;
    const pctSaturation = cs > 0 ? (cdDownstream / cs) * 100 : 0;

    stepResults.push({
      stepNum: i + 1,
      h,
      r,
      cuUpstream: cuCurrent,
      cdDownstream,
      doGain,
      pctSaturation,
    });

    cuCurrent = cdDownstream; // Cd of this step → Cu of next
  }

  const finalDO = stepResults.length > 0 ? stepResults[stepResults.length - 1].cdDownstream : p.inletDO;
  const finalPctSat = cs > 0 ? (finalDO / cs) * 100 : 0;
  const totalDOAbsorption = finalDO - p.inletDO;

  // DO absorption rate (kg/h)
  const doAbsorptionRate = (totalDOAbsorption * p.flowRate) / 1000; // mg/L × m³/h → kg/h (×1000/1000)

  // Weir hydraulics — sharp-crested weir
  const h1 = solveWeirHead(p.flowRate, p.weirLength, p.weirHeight);
  const weirLoadingRate = (p.flowRate / 3.6) / p.weirLength; // L/s/m (flowRate m³/h → L/s = /3.6)

  // Tower geometry
  const towerVolume = (Math.PI / 4) * p.towerDiameter * p.towerDiameter * p.towerHeight; // m³

  // Forced ventilation sizing
  // Air required: O2 absorption / ((O2in% - O2out%) / 100 × air density)
  const o2InPct = 21;
  const o2OutPct = p.exhaustO2Pct;
  const airDensity = 1.293; // kg/Nm³
  let airRequirement = 0;
  if (o2InPct > o2OutPct) {
    airRequirement = doAbsorptionRate / (((o2InPct - o2OutPct) / 100) * airDensity);
  }
  const airChanges = towerVolume > 0 ? airRequirement / towerVolume : 0;

  // r_total (product of all individual r values)
  const rTotal = stepResults.reduce((prod, s) => prod * s.r, 1);

  // Validation
  const vWeirLoading = weirLoadingRate <= 36;
  const vFinalDO = finalDO >= p.targetDO;
  const vStepHeightMax = p.steps.map(s => s.h <= 3.0);
  const vStepHeightMin = p.steps.map(s => s.h >= 0.15);
  const vAirChanges = airChanges >= 5;
  const vStepR = stepResults.map(s => s.r >= 1.05);

  return {
    cs, steps: stepResults,
    finalDO, finalPctSat,
    totalDOAbsorption, doAbsorptionRate,
    weirLoadingRate, headOverWeir: h1,
    airRequirement, airChanges,
    towerVolume, rTotal,
    vWeirLoading, vFinalDO,
    vStepHeightMax, vStepHeightMin,
    vAirChanges, vStepR,
  };
}

// ─── Utility ─────────────────────────────────────────────

const fmt = (v: number, d = 1): string =>
  (typeof v === 'number' && isFinite(v) ? v : 0).toFixed(d);

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];

// ─── Sub-components ──────────────────────────────────────

function S({
  label, value, min, max, step, unit,
  icon: Icon, color = '#06b6d4', onChange, info,
}: {
  label: string; value: number; min: number; max: number; step: number;
  unit: string; icon: React.ElementType; color?: string;
  onChange: (v: number) => void; info?: string;
}) {
  const safe = isFinite(value) ? value : min;
  const decimals = step < 0.1 ? 3 : step < 1 ? 2 : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
          {info && (
            <div className="group relative">
              <Info className="w-3 h-3 text-slate-600 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 p-2 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 rb-card-sm">
                {info}
              </div>
            </div>
          )}
        </div>
        <span className="text-xs font-bold text-white tabular-nums">
          {safe.toFixed(decimals)}
          <span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span>
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={safe}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
        style={{ accentColor: color }}
      />
    </div>
  );
}

function ResultBadge({ pass, label }: { pass: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${pass ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>
      {pass ? <CheckCircle2 className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />}
      {label}
    </span>
  );
}

function ValidationRow({ pass, label, detail }: { pass: boolean; label: string; detail: string }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${pass ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rose-500/5 border-rose-500/20'}`}>
      {pass
        ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
        : <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />}
      <div className="min-w-0">
        <p className={`text-xs font-bold ${pass ? 'text-emerald-400' : 'text-rose-400'}`}>{label}</p>
        <p className="text-[10px] text-slate-500 truncate">{detail}</p>
      </div>
    </div>
  );
}

// ─── DO Profile Chart ────────────────────────────────────

function DOProfileChart({ results }: { results: CascadeResults }) {
  const data = [
    { step: 'Inlet', DO: results.steps[0]?.cuUpstream ?? 0, sat: results.cs },
    ...results.steps.map(s => ({
      step: ORDINALS[s.stepNum - 1] ?? `Step ${s.stepNum}`,
      DO: s.cdDownstream,
      sat: results.cs,
    })),
  ];

  return (
    <div className="p-4 rb-card">
      <p className="text-xs font-bold text-slate-300 mb-3 flex items-center gap-2">
        <TrendingUp className="w-3.5 h-3.5 text-sky-400" />
        DO Profile — Step by Step
      </p>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="step" tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} unit=" mg/L" domain={[0, Math.ceil(results.cs + 1)]} />
          <Tooltip
            contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#e2e8f0', fontWeight: 700 }}
          />
          <Legend wrapperStyle={{ fontSize: 10, color: '#94a3b8' }} />
          <ReferenceLine y={results.cs} stroke="#f59e0b" strokeDasharray="6 3" label={{ value: `Sat ${fmt(results.cs, 2)} mg/L`, position: 'right', fill: '#f59e0b', fontSize: 9 }} />
          <Line type="monotone" dataKey="DO" stroke="#06b6d4" strokeWidth={2.5} dot={{ fill: '#06b6d4', r: 4 }} name="DO (mg/L)" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Deficit Ratio Bar Chart ─────────────────────────────

function DeficitRatioChart({ results }: { results: CascadeResults }) {
  const data = results.steps.map(s => ({
    step: ORDINALS[s.stepNum - 1] ?? `Step ${s.stepNum}`,
    r: parseFloat(fmt(s.r, 3)),
    doGain: parseFloat(fmt(s.doGain, 2)),
  }));

  return (
    <div className="p-4 rb-card">
      <p className="text-xs font-bold text-slate-300 mb-3 flex items-center gap-2">
        <BarChart3 className="w-3.5 h-3.5 text-violet-400" />
        Deficit Ratio & DO Gain per Step
      </p>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="step" tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <YAxis yAxisId="r" tick={{ fill: '#94a3b8', fontSize: 10 }} domain={[1, 'auto']} label={{ value: 'r', angle: -90, position: 'insideLeft', fill: '#8b5cf6', fontSize: 10 }} />
          <YAxis yAxisId="gain" orientation="right" tick={{ fill: '#94a3b8', fontSize: 10 }} unit=" mg/L" />
          <Tooltip
            contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#e2e8f0', fontWeight: 700 }}
          />
          <Legend wrapperStyle={{ fontSize: 10, color: '#94a3b8' }} />
          <Bar yAxisId="r" dataKey="r" fill="#8b5cf6" opacity={0.85} name="Deficit ratio r" radius={[3, 3, 0, 0]} />
          <Bar yAxisId="gain" dataKey="doGain" fill="#06b6d4" opacity={0.85} name="DO gain (mg/L)" radius={[3, 3, 0, 0]} />
          <ReferenceLine yAxisId="r" y={1.05} stroke="#f59e0b" strokeDasharray="4 2" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Process Flow Diagram ─────────────────────────────────

function CascadeFlowDiagram({ params, results }: { params: CascadeParams; results: CascadeResults }) {
  const n = params.steps.length;
  const stepW = Math.min(120, Math.floor(800 / (n + 2)));
  const svgW = (n + 2) * stepW + 40;
  const svgH = 200;

  return (
    <div className="p-4 overflow-x-auto rb-card">
      <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
        <ArrowDown className="w-3 h-3" /> Cascade Tower — Step Process Train
      </p>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto min-w-[500px]">
        <defs>
          <linearGradient id="waterGradC" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#0f2847" stopOpacity="0.9" />
          </linearGradient>
        </defs>

        {/* Inlet box */}
        <rect x="10" y="20" width={stepW - 10} height="60" rx="6" fill="url(#waterGradC)" stroke="#f59e0b" strokeWidth="1.5" />
        <text x={10 + (stepW - 10) / 2} y="40" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">Inlet</text>
        <text x={10 + (stepW - 10) / 2} y="54" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.flowRate, 0)} m³/h</text>
        <text x={10 + (stepW - 10) / 2} y="67" fill="#06b6d4" fontSize="8" textAnchor="middle">DO {fmt(params.inletDO, 1)} mg/L</text>
        <text x={10 + (stepW - 10) / 2} y="79" fill="#94a3b8" fontSize="8" textAnchor="middle">T={fmt(params.temperature, 0)}°C</text>

        {/* Step boxes */}
        {results.steps.map((s, i) => {
          const x = (i + 1) * stepW + 10;
          const pass = results.vStepR[i] && results.vStepHeightMax[i] && results.vStepHeightMin[i];
          return (
            <g key={i}>
              {/* Arrow */}
              <line x1={x - 10} y1="50" x2={x + 2} y2="50" stroke="#06b6d4" strokeWidth="1.5" strokeDasharray="4 2" />
              <polygon points={`${x},47 ${x + 7},50 ${x},53`} fill="#06b6d4" />
              {/* Box */}
              <rect x={x + 8} y="20" width={stepW - 18} height="70" rx="5" fill="url(#waterGradC)" stroke={pass ? '#06b6d4' : '#f43f5e'} strokeWidth="1.5" />
              <text x={x + 8 + (stepW - 18) / 2} y="36" fill={pass ? '#06b6d4' : '#f43f5e'} fontSize="8" textAnchor="middle" fontWeight="bold">{ORDINALS[i]}</text>
              <text x={x + 8 + (stepW - 18) / 2} y="48" fill="#94a3b8" fontSize="7" textAnchor="middle">h={fmt(s.h, 2)}m</text>
              <text x={x + 8 + (stepW - 18) / 2} y="58" fill="#8b5cf6" fontSize="7" textAnchor="middle">r={fmt(s.r, 3)}</text>
              <text x={x + 8 + (stepW - 18) / 2} y="69" fill="#10b981" fontSize="7" textAnchor="middle">DO↑{fmt(s.doGain, 2)}</text>
              <text x={x + 8 + (stepW - 18) / 2} y="80" fill="#06b6d4" fontSize="7" textAnchor="middle">{fmt(s.cdDownstream, 2)} mg/L</text>
              {/* Fall water animation */}
              <motion.rect x={x + 8 + (stepW - 18) / 2 - 3} y="90" width="6" height="20" rx="2" fill="#06b6d4" opacity="0.25"
                animate={{ y: [90, 105, 90], opacity: [0.25, 0.5, 0.25] }}
                transition={{ repeat: Infinity, duration: 1.5 + i * 0.2 }} />
            </g>
          );
        })}

        {/* Final effluent box */}
        {(() => {
          const x = (n + 1) * stepW + 10;
          return (
            <g>
              <line x1={x - 10} y1="50" x2={x + 2} y2="50" stroke="#10b981" strokeWidth="1.5" strokeDasharray="4 2" />
              <polygon points={`${x},47 ${x + 7},50 ${x},53`} fill="#10b981" />
              <rect x={x + 8} y="20" width={stepW - 18} height="70" rx="5" fill="url(#waterGradC)" stroke="#10b981" strokeWidth="2" />
              <text x={x + 8 + (stepW - 18) / 2} y="36" fill="#10b981" fontSize="8" textAnchor="middle" fontWeight="bold">Effluent</text>
              <text x={x + 8 + (stepW - 18) / 2} y="50" fill="#06b6d4" fontSize="8" textAnchor="middle">{fmt(results.finalDO, 2)} mg/L</text>
              <text x={x + 8 + (stepW - 18) / 2} y="62" fill="#10b981" fontSize="7" textAnchor="middle">{fmt(results.finalPctSat, 0)}% sat</text>
              <text x={x + 8 + (stepW - 18) / 2} y="75" fill="#94a3b8" fontSize="7" textAnchor="middle">Cs={fmt(results.cs, 2)}</text>
            </g>
          );
        })()}

        {/* Wind arrows (ventilation) */}
        {[0.25, 0.5, 0.75].map((pct, i) => (
          <g key={`wind-${i}`}>
            <motion.line x1={svgW * pct - 8} y1={svgH - 30} x2={svgW * pct + 8} y2={svgH - 45}
              stroke="#6366f1" strokeWidth="1" strokeDasharray="2 2" opacity={0.5}
              animate={{ opacity: [0.3, 0.7, 0.3] }}
              transition={{ repeat: Infinity, duration: 2, delay: i * 0.4 }} />
          </g>
        ))}
        <text x={svgW / 2} y={svgH - 15} fill="#6366f1" fontSize="8" textAnchor="middle" opacity="0.7">
          ↑ Forced ventilation {fmt(results.airRequirement, 0)} Nm³/h · {fmt(results.airChanges, 1)} air changes/h
        </text>
      </svg>
    </div>
  );
}

// ─── Print Report Modal ───────────────────────────────────

function PrintReportModal({ params, results, onClose }: {
  params: CascadeParams;
  results: CascadeResults;
  onClose: () => void;
}) {
  const allValid = results.vWeirLoading && results.vFinalDO && results.vAirChanges &&
    results.vStepR.every(Boolean) &&
    results.vStepHeightMax.every(Boolean) &&
    results.vStepHeightMin.every(Boolean);

  const validations: { pass: boolean; label: string; detail: string }[] = [
    { pass: results.vWeirLoading, label: 'Weir loading ≤ 36 L/s/m', detail: `Actual: ${fmt(results.weirLoadingRate, 2)} L/s/m` },
    { pass: results.vFinalDO, label: `Final DO ≥ ${fmt(params.targetDO, 1)} mg/L`, detail: `Achieved: ${fmt(results.finalDO, 2)} mg/L (${fmt(results.finalPctSat, 0)}% sat)` },
    { pass: results.vStepHeightMax.every(Boolean), label: 'All step heights ≤ 3.0 m', detail: `Max: ${fmt(Math.max(...params.steps.map(s => s.h)), 2)} m` },
    { pass: results.vStepHeightMin.every(Boolean), label: 'All step heights ≥ 0.15 m', detail: `Min: ${fmt(Math.min(...params.steps.map(s => s.h)), 2)} m` },
    { pass: results.vAirChanges, label: 'Air changes ≥ 5/h', detail: `Actual: ${fmt(results.airChanges, 1)}/h` },
    { pass: results.vStepR.every(Boolean), label: 'All step r ≥ 1.05', detail: `Min r: ${fmt(Math.min(...results.steps.map(s => s.r)), 3)}` },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8"
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          className="w-full max-w-3xl mx-4 overflow-hidden rb-section"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
                <Printer className="w-4 h-4 text-sky-400" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Cascade Aerator — Validation Report</h2>
                <p className="text-xs text-slate-500">Ref: Twort, Ratnayaka & Brandt, Water Supply 5th Ed., 1988, p390–391</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-500/10 border border-sky-500/30 rounded-lg text-xs font-bold text-sky-400 hover:bg-sky-500/20 transition-colors"
              >
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-800 transition-colors">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6 print:p-4">
            {/* Overall status */}
            <div className={`p-4 rounded-xl border ${allValid ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
              <div className="flex items-center gap-2">
                {allValid
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  : <AlertCircle className="w-5 h-5 text-amber-400" />}
                <span className={`font-bold ${allValid ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {allValid ? 'All criteria PASSED — design is compliant' : 'One or more criteria FAILED — review required'}
                </span>
              </div>
            </div>

            {/* Input parameters */}
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Input Parameters</h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ['Flow rate', `${fmt(params.flowRate, 0)} m³/h`],
                  ['Inlet DO (Cu₀)', `${fmt(params.inletDO, 1)} mg/L`],
                  ['Temperature', `${fmt(params.temperature, 0)} °C`],
                  ['DO Saturation Cs', `${fmt(results.cs, 3)} mg/L`],
                  ['Water quality factor a', `${params.waterQualityA} (${params.waterQualityA === 1.25 ? 'slightly polluted' : params.waterQualityA === 1.0 ? 'moderately polluted' : 'sewage'})`],
                  ['Weir type factor b', `${params.weirTypeB} (${params.weirTypeB === 1.3 ? 'stepped' : 'free-fall'})`],
                  ['Number of steps', `${params.steps.length}`],
                  ['Step heights (m)', params.steps.map(s => fmt(s.h, 3)).join(', ')],
                  ['Weir length b', `${fmt(params.weirLength, 1)} m`],
                  ['Weir height p1', `${fmt(params.weirHeight, 2)} m`],
                  ['Tower diameter', `${fmt(params.towerDiameter, 1)} m`],
                  ['Tower height', `${fmt(params.towerHeight, 3)} m`],
                  ['Inlet O₂', `21%`],
                  ['Exhaust O₂', `${fmt(params.exhaustO2Pct, 1)}%`],
                  ['Target effluent DO', `${fmt(params.targetDO, 1)} mg/L`],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-start gap-1 p-2 rounded-lg bg-rb-elevated">
                    <span className="text-slate-500 shrink-0">{k}:</span>
                    <span className="text-slate-200 font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Per-step results table */}
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Per-Step Results</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-700">
                      {['Step', 'h (m)', 'r', 'Cu upstream', 'Cd downstream', 'DO gain', '% Sat', 'Status'].map(h => (
                        <th key={h} className="text-left py-2 px-2 text-slate-500 font-bold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.steps.map((s, i) => {
                      const pass = results.vStepR[i] && results.vStepHeightMax[i] && results.vStepHeightMin[i];
                      return (
                        <tr key={i} className="border-b border-slate-800">
                          <td className="py-2 px-2 font-bold text-slate-200">{ORDINALS[i]}</td>
                          <td className="py-2 px-2 text-slate-300">{fmt(s.h, 3)}</td>
                          <td className="py-2 px-2 text-violet-400 font-mono">{fmt(s.r, 4)}</td>
                          <td className="py-2 px-2 text-amber-400 font-mono">{fmt(s.cuUpstream, 3)}</td>
                          <td className="py-2 px-2 text-sky-400 font-mono">{fmt(s.cdDownstream, 3)}</td>
                          <td className="py-2 px-2 text-emerald-400 font-mono">+{fmt(s.doGain, 3)}</td>
                          <td className="py-2 px-2 text-slate-300">{fmt(s.pctSaturation, 1)}%</td>
                          <td className="py-2 px-2">
                            <ResultBadge pass={pass} label={pass ? 'Pass' : 'Fail'} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary results */}
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Summary Results</h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ['Final effluent DO', `${fmt(results.finalDO, 3)} mg/L (${fmt(results.finalPctSat, 1)}% of Cs)`],
                  ['Total DO absorption', `${fmt(results.totalDOAbsorption, 3)} mg/L`],
                  ['DO absorption rate', `${fmt(results.doAbsorptionRate, 3)} kg/h`],
                  ['Weir loading rate', `${fmt(results.weirLoadingRate, 2)} L/s/m (limit 36)`],
                  ['Head over weir h1', `${fmt(results.headOverWeir, 4)} m`],
                  ['Tower volume', `${fmt(results.towerVolume, 2)} m³`],
                  ['Air requirement', `${fmt(results.airRequirement, 1)} Nm³/h`],
                  ['Air changes per hour', `${fmt(results.airChanges, 2)}/h`],
                  ['r_total (all steps)', `${fmt(results.rTotal, 4)}`],
                  ['DO Saturation Cs(T)', `${fmt(results.cs, 3)} mg/L at ${fmt(params.temperature, 0)}°C`],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-start gap-1 p-2 rounded-lg bg-rb-elevated">
                    <span className="text-slate-500 shrink-0">{k}:</span>
                    <span className="text-slate-200 font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Formula derivations */}
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Formula Derivations</h3>
              <div className="space-y-2 text-xs p-4 font-mono rb-inner-card">
                <p className="text-slate-400 font-sans font-bold">DO Saturation (Twort-Ratnayaka-Brandt polynomial):</p>
                <p className="text-sky-300">Cs(T) = 14.621 − 0.39363·T + 0.007714·T² − 0.0000646·T³  [mg/L]</p>
                <p className="text-slate-500">= 14.621 − 0.39363·{fmt(params.temperature, 0)} + 0.007714·{fmt(params.temperature * params.temperature, 0)} − 0.0000646·{fmt(params.temperature ** 3, 0)} = {fmt(results.cs, 3)} mg/L</p>
                <p className="text-slate-400 font-sans font-bold mt-3">Deficit Ratio per Step:</p>
                <p className="text-sky-300">r = 1 + 0.38 × a × b × h × (1 − 0.11·h) × (1 + 0.046·T)</p>
                <p className="text-slate-500">a = {params.waterQualityA}, b = {params.weirTypeB}, T = {fmt(params.temperature, 0)}°C</p>
                <p className="text-slate-400 font-sans font-bold mt-3">DO Downstream per Step:</p>
                <p className="text-sky-300">Cd = Cs − (Cs − Cu) / r</p>
                <p className="text-slate-400 font-sans font-bold mt-3">Sharp-Crested Weir (Casey):</p>
                <p className="text-sky-300">Q = Ce × (2/3) × √(2g) × be × he^1.5</p>
                <p className="text-sky-300">Ce = 0.602 + 0.075 × (h1/p1),  be = b − 0.0009,  he = h1 + 0.001</p>
                <p className="text-slate-500">h1 solved iteratively: {fmt(results.headOverWeir, 4)} m</p>
                <p className="text-slate-400 font-sans font-bold mt-3">Forced Ventilation:</p>
                <p className="text-sky-300">Air = DO_absorption / ((O2in% − O2out%) / 100 × 1.293)  [Nm³/h]</p>
                <p className="text-slate-500">= {fmt(results.doAbsorptionRate, 3)} / ({params.exhaustO2Pct > 0 ? `(21 − ${fmt(params.exhaustO2Pct, 0)}) / 100 × 1.293` : '—'}) = {fmt(results.airRequirement, 1)} Nm³/h</p>
              </div>
            </div>

            {/* Validation summary */}
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Validation Summary</h3>
              <div className="space-y-2">
                {validations.map((v, i) => (
                  <ValidationRow key={i} pass={v.pass} label={v.label} detail={v.detail} />
                ))}
              </div>
            </div>

            <p className="text-[10px] text-slate-600 border-t border-slate-800 pt-4">
              Reference: Twort A.C., Ratnayaka D.D. & Brandt M.J., Water Supply 5th Edition, 1988, pp 390–391. Weir hydraulics: Casey, Water &amp; Wastewater Engineering Hydraulics. Generated {new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' })} by Reynolds Bauhm Smart Water Tool.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════
export default function CascadeAeratorSpecPage() {
  const [params, setParams] = useState<CascadeParams>(DEFAULT_PARAMS);
  const [showReport, setShowReport] = useState(false);
  const [activeTab, setActiveTab] = useState<'inlet' | 'steps' | 'weir' | 'tower'>('inlet');

  const results = useMemo(() => calcCascade(params), [params]);

  const set = useCallback(<K extends keyof CascadeParams>(key: K, value: CascadeParams[K]) => {
    setParams(prev => ({ ...prev, [key]: value }));
  }, []);

  const setStep = useCallback((index: number, h: number) => {
    setParams(prev => {
      const newSteps = [...prev.steps];
      newSteps[index] = { h };
      return { ...prev, steps: newSteps };
    });
  }, []);

  const addStep = useCallback(() => {
    setParams(prev => {
      if (prev.steps.length >= 8) { toast.warning('Maximum 8 steps allowed'); return prev; }
      return { ...prev, steps: [...prev.steps, { h: 0.40 }] };
    });
  }, []);

  const removeStep = useCallback((index: number) => {
    setParams(prev => {
      if (prev.steps.length <= 2) { toast.warning('Minimum 2 steps required'); return prev; }
      const newSteps = prev.steps.filter((_, i) => i !== index);
      return { ...prev, steps: newSteps };
    });
  }, []);

  const handleReset = () => {
    setParams(DEFAULT_PARAMS);
    toast.info('Parameters reset to Excel defaults');
  };

  // Auto-calculate tower height = sum of step heights + 20% freeboard
  const autoTowerHeight = useMemo(() => {
    const sumH = params.steps.reduce((s, st) => s + st.h, 0);
    return parseFloat((sumH * 1.2).toFixed(3));
  }, [params.steps]);

  // Science accordion
  const scienceSections = useMemo(() => {
    const T = params.temperature;
    const a = params.waterQualityA;
    const b = params.weirTypeB;
    const cs = results.cs;
    const Q = params.flowRate;
    const h_ex = params.steps[0]?.h ?? 0.56;
    const r_ex = results.steps[0]?.r ?? 1;
    const cu_ex = params.inletDO;
    const cd_ex = results.steps[0]?.cdDownstream ?? cu_ex;

    return [
      {
        title: 'DO Saturation — Twort-Ratnayaka-Brandt Polynomial',
        icon: Thermometer,
        equations: [
          {
            label: 'DO Saturation Cs(T)',
            formula: 'Cs(T) = 14.621 − 0.39363·T + 0.007714·T² − 0.0000646·T³',
            substitution: `Cs(${T}) = 14.621 − 0.39363×${T} + 0.007714×${T}² − 0.0000646×${T}³ = ${fmt(cs, 4)} mg/L`,
            result: fmt(cs, 4), status: 'pass' as const, unit: 'mg/L',
          },
          {
            label: 'Fallback Benson (check)',
            formula: 'Cs = 468 / (31.6 + T)',
            substitution: `468 / (31.6 + ${T}) = ${fmt(468 / (31.6 + T), 3)} mg/L`,
            result: fmt(468 / (31.6 + T), 3), status: 'pass' as const, unit: 'mg/L',
          },
        ],
      },
      {
        title: 'Deficit Ratio per Step (Twort-Ratnayaka-Brandt)',
        icon: Waves,
        equations: [
          {
            label: 'General formula',
            formula: 'r = 1 + 0.38 × a × b × h × (1 − 0.11·h) × (1 + 0.046·T)',
            substitution: `r = 1 + 0.38 × ${a} × ${b} × ${fmt(h_ex, 3)} × (1 − 0.11×${fmt(h_ex, 3)}) × (1 + 0.046×${T}) = ${fmt(r_ex, 4)}`,
            result: fmt(r_ex, 4), status: r_ex >= 1.05 ? 'pass' as const : 'fail' as const, unit: '',
          },
          {
            label: 'DO downstream Cd (step 1)',
            formula: 'Cd = Cs − (Cs − Cu) / r',
            substitution: `Cd = ${fmt(cs, 3)} − (${fmt(cs, 3)} − ${fmt(cu_ex, 2)}) / ${fmt(r_ex, 4)} = ${fmt(cd_ex, 4)} mg/L`,
            result: fmt(cd_ex, 4), status: 'pass' as const, unit: 'mg/L',
          },
          {
            label: 'r_total (product all steps)',
            formula: 'r_total = r₁ × r₂ × … × rₙ',
            substitution: results.steps.map(s => fmt(s.r, 3)).join(' × ') + ` = ${fmt(results.rTotal, 4)}`,
            result: fmt(results.rTotal, 4), status: 'pass' as const, unit: '',
          },
        ],
      },
      {
        title: 'Sharp-Crested Weir Hydraulics (Casey)',
        icon: Gauge,
        equations: [
          {
            label: 'Weir flow equation',
            formula: 'Q = Ce × (2/3) × √(2g) × be × he^1.5',
            substitution: `Ce = 0.602 + 0.075×(h1/${params.weirHeight}), be = ${params.weirLength} − 0.0009, he = h1 + 0.001`,
            result: fmt(results.headOverWeir, 4), status: 'pass' as const, unit: 'm (h1)',
          },
          {
            label: 'Head over weir h1 (iterated)',
            formula: 'Solve iteratively for h1 given Q, b, p1',
            substitution: `Q = ${fmt(Q, 0)} m³/h, b = ${params.weirLength} m, p1 = ${params.weirHeight} m → h1 = ${fmt(results.headOverWeir, 4)} m`,
            result: fmt(results.headOverWeir, 4), status: 'pass' as const, unit: 'm',
          },
          {
            label: 'Weir loading rate',
            formula: 'WL = Q / (3.6 × b)  [L/s/m]',
            substitution: `WL = ${fmt(Q, 0)} / (3.6 × ${params.weirLength}) = ${fmt(results.weirLoadingRate, 2)} L/s/m`,
            result: fmt(results.weirLoadingRate, 2),
            status: results.vWeirLoading ? 'pass' as const : 'fail' as const,
            unit: 'L/s/m',
          },
        ],
        warnings: results.vWeirLoading ? [] : [`Weir loading ${fmt(results.weirLoadingRate, 2)} L/s/m exceeds 36 L/s/m limit`],
      },
      {
        title: 'Forced Ventilation Sizing',
        icon: Wind,
        equations: [
          {
            label: 'O₂ absorbed (kg/h)',
            formula: 'DO_abs = (Cd_final − Cu₀) × Q  [kg/h]',
            substitution: `DO_abs = (${fmt(results.finalDO, 3)} − ${fmt(params.inletDO, 1)}) × ${fmt(Q, 0)} / 1000 = ${fmt(results.doAbsorptionRate, 3)} kg/h`,
            result: fmt(results.doAbsorptionRate, 3), status: 'pass' as const, unit: 'kg/h',
          },
          {
            label: 'Air required (Nm³/h)',
            formula: 'Air = DO_abs / ((O2in% − O2out%) / 100 × 1.293)',
            substitution: `Air = ${fmt(results.doAbsorptionRate, 3)} / ((21 − ${params.exhaustO2Pct}) / 100 × 1.293) = ${fmt(results.airRequirement, 2)} Nm³/h`,
            result: fmt(results.airRequirement, 2), status: 'pass' as const, unit: 'Nm³/h',
          },
          {
            label: 'Tower volume',
            formula: 'V = π/4 × D² × H',
            substitution: `V = π/4 × ${params.towerDiameter}² × ${params.towerHeight} = ${fmt(results.towerVolume, 2)} m³`,
            result: fmt(results.towerVolume, 2), status: 'pass' as const, unit: 'm³',
          },
          {
            label: 'Air changes per hour',
            formula: 'ACH = Air_required / Tower_volume',
            substitution: `ACH = ${fmt(results.airRequirement, 2)} / ${fmt(results.towerVolume, 2)} = ${fmt(results.airChanges, 2)}/h`,
            result: fmt(results.airChanges, 2),
            status: results.vAirChanges ? 'pass' as const : 'fail' as const,
            unit: '/h',
          },
        ],
        warnings: results.vAirChanges ? [] : [`Air changes ${fmt(results.airChanges, 2)}/h below minimum 5/h`],
      },
    ];
  }, [params, results]);

  const allValid = results.vWeirLoading && results.vFinalDO && results.vAirChanges &&
    results.vStepR.every(Boolean) &&
    results.vStepHeightMax.every(Boolean) &&
    results.vStepHeightMin.every(Boolean);

  const tabClass = (t: typeof activeTab) =>
    `px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${activeTab === t ? 'bg-sky-500/15 text-sky-400 border border-sky-500/25' : 'text-slate-500 hover:text-slate-300'}`;

  return (
    <div className="space-y-6 pb-12">
      {/* ─── Header ─────────────────────────────────────── */}
      <PageHeader
        icon={Layers}
        title="Cascade Aerator Specification"
        subtitle="Multi-step aeration tower — Twort, Ratnayaka & Brandt, Water Supply 5th Ed. (1988) p390–391"
        iconColor="text-sky-400"
        actions={
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${allValid ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border-rose-500/20 text-rose-400'}`}>
              {allValid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              {allValid ? 'All criteria met' : 'Review required'}
            </div>
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs font-medium text-slate-300 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
            <button
              onClick={() => setShowReport(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-xs font-bold text-sky-400 transition-colors"
            >
              <FileText className="w-3.5 h-3.5" /> Generate Report
            </button>
          </div>
        }
      />

      {/* ─── Key summary cards ───────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: 'Final Effluent DO',
            value: fmt(results.finalDO, 2),
            unit: 'mg/L',
            sub: `${fmt(results.finalPctSat, 1)}% saturation`,
            color: results.vFinalDO ? '#10b981' : '#f43f5e',
            icon: Droplets,
          },
          {
            label: 'DO Saturation Cs',
            value: fmt(results.cs, 3),
            unit: 'mg/L',
            sub: `at ${fmt(params.temperature, 0)}°C`,
            color: '#06b6d4',
            icon: Thermometer,
          },
          {
            label: 'Weir Loading',
            value: fmt(results.weirLoadingRate, 2),
            unit: 'L/s/m',
            sub: results.vWeirLoading ? 'Within limit ≤ 36' : 'Exceeds limit!',
            color: results.vWeirLoading ? '#10b981' : '#f43f5e',
            icon: Gauge,
          },
          {
            label: 'Air Changes',
            value: fmt(results.airChanges, 1),
            unit: '/h',
            sub: results.vAirChanges ? 'Min 5/h met' : 'Below minimum!',
            color: results.vAirChanges ? '#10b981' : '#f43f5e',
            icon: Wind,
          },
        ].map(card => (
          <div key={card.label} className="p-4 rb-card">
            <div className="flex items-center gap-2 mb-2">
              <card.icon className="w-4 h-4" style={{ color: card.color }} />
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{card.label}</span>
            </div>
            <div className="text-2xl font-bold tabular-nums" style={{ color: card.color }}>
              {card.value} <span className="text-sm font-normal text-slate-500">{card.unit}</span>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">{card.sub}</div>
          </div>
        ))}
      </div>

      {/* ─── Main layout ─────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6">
        {/* ── Left panel: Parameters ─────────────────────── */}
        <div className="space-y-4">
          {/* Tab navigation */}
          <div className="flex gap-1 flex-wrap">
            {(['inlet', 'steps', 'weir', 'tower'] as const).map(t => (
              <button key={t} onClick={() => setActiveTab(t)} className={tabClass(t)}>
                {t === 'inlet' ? 'Inlet' : t === 'steps' ? 'Steps' : t === 'weir' ? 'Weir' : 'Tower'}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
            >
              {/* ── Inlet Conditions ───────────────────────── */}
              {activeTab === 'inlet' && (
                <div className="p-4 space-y-4 rb-card">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold flex items-center gap-1.5">
                    <Droplets className="w-3 h-3 text-sky-400" /> Inlet Conditions
                  </p>
                  <S label="Flow rate" value={params.flowRate} min={10} max={5000} step={1} unit="m³/h"
                    icon={Gauge} color="#f59e0b" onChange={v => set('flowRate', v)}
                    info="Design flow entering the cascade tower" />
                  <S label="Inlet DO (Cu₀)" value={params.inletDO} min={0} max={8} step={0.1} unit="mg/L"
                    icon={Droplets} color="#06b6d4" onChange={v => set('inletDO', v)}
                    info="Dissolved oxygen at tower inlet — often 0 for anaerobic groundwater" />
                  <S label="Temperature" value={params.temperature} min={1} max={30} step={0.5} unit="°C"
                    icon={Thermometer} color="#ef4444" onChange={v => set('temperature', v)}
                    info="Water temperature, affects DO saturation and deficit ratio" />

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Microscope className="w-3.5 h-3.5 text-violet-400" />
                      <span className="text-xs font-medium text-slate-300">Water quality factor a</span>
                      <div className="group relative ml-auto">
                        <Info className="w-3 h-3 text-slate-600 cursor-help" />
                        <div className="absolute bottom-full right-0 mb-2 w-56 p-2 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 pointer-events-none z-50 rb-card-sm">
                          Twort factor a: accounts for organic pollution. 1.25 = slightly polluted, 1.0 = moderately polluted, 0.85 = sewage-contaminated.
                        </div>
                      </div>
                    </div>
                    <select
                      value={params.waterQualityA}
                      onChange={e => set('waterQualityA', parseFloat(e.target.value))}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-sky-500"
                    >
                      <option value={1.25}>1.25 — Slightly polluted</option>
                      <option value={1.0}>1.00 — Moderately polluted</option>
                      <option value={0.85}>0.85 — Sewage-contaminated</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Waves className="w-3.5 h-3.5 text-sky-400" />
                      <span className="text-xs font-medium text-slate-300">Weir type factor b</span>
                    </div>
                    <select
                      value={params.weirTypeB}
                      onChange={e => set('weirTypeB', parseFloat(e.target.value))}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-sky-500"
                    >
                      <option value={1.0}>1.0 — Free-fall weir</option>
                      <option value={1.3}>1.3 — Stepped weir (enhanced)</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Droplets className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-xs font-medium text-slate-300">Target effluent DO</span>
                    </div>
                    <input
                      type="number" min={4} max={12} step={0.1}
                      value={params.targetDO}
                      onChange={e => set('targetDO', parseFloat(e.target.value))}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-sky-500"
                    />
                  </div>

                  {/* Cs info */}
                  <div className="p-3 flex items-center gap-3 rb-inner-card-sm">
                    <Thermometer className="w-4 h-4 text-sky-400 shrink-0" />
                    <div>
                      <p className="text-[10px] text-slate-500">Calculated DO Saturation Cs</p>
                      <p className="text-sm font-bold text-sky-400">{fmt(results.cs, 3)} mg/L @ {fmt(params.temperature, 0)}°C</p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Cascade Steps ───────────────────────────── */}
              {activeTab === 'steps' && (
                <div className="p-4 space-y-4 rb-card">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold flex items-center gap-1.5">
                      <ArrowDown className="w-3 h-3 text-violet-400" /> Cascade Steps ({params.steps.length})
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={addStep}
                        disabled={params.steps.length >= 8}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-sky-500/10 border border-sky-500/20 text-sky-400 hover:bg-sky-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {params.steps.map((step, i) => {
                      const r = results.steps[i];
                      const pass = r ? (results.vStepR[i] && results.vStepHeightMax[i] && results.vStepHeightMin[i]) : true;
                      return (
                        <div key={i} className={`rounded-lg border p-3 ${pass ? 'border-slate-800 bg-rb-elevated' : 'border-rose-500/30 bg-rose-500/5'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs font-bold ${pass ? 'text-slate-300' : 'text-rose-400'}`}>
                              {ORDINALS[i]} step
                              {r && (
                                <span className="ml-2 text-[10px] font-normal text-slate-500">
                                  r = <span className="text-violet-400">{fmt(r.r, 4)}</span>
                                  &nbsp;·&nbsp;DO: {fmt(r.cuUpstream, 2)} → <span className="text-emerald-400">{fmt(r.cdDownstream, 2)}</span> mg/L
                                  &nbsp;·&nbsp;<span className="text-sky-400">{fmt(r.pctSaturation, 0)}% sat</span>
                                </span>
                              )}
                            </span>
                            <button
                              onClick={() => removeStep(i)}
                              disabled={params.steps.length <= 2}
                              className="w-6 h-6 flex items-center justify-center rounded text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                          </div>
                          <S
                            label={`Fall height h`}
                            value={step.h}
                            min={0.05}
                            max={3.0}
                            step={0.01}
                            unit="m"
                            icon={ArrowDown}
                            color={pass ? '#8b5cf6' : '#f43f5e'}
                            onChange={v => setStep(i, v)}
                            info="Vertical fall height for this step. Max 3.0 m structural, min 0.15 m practical."
                          />
                          {!results.vStepHeightMax[i] && (
                            <p className="text-[10px] text-rose-400 mt-1">⚠ Exceeds 3.0 m structural limit</p>
                          )}
                          {!results.vStepHeightMin[i] && (
                            <p className="text-[10px] text-amber-400 mt-1">⚠ Below 0.15 m practical minimum</p>
                          )}
                          {r && !results.vStepR[i] && (
                            <p className="text-[10px] text-amber-400 mt-1">⚠ r = {fmt(r.r, 4)} below minimum 1.05 (negligible aeration)</p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="p-3 text-xs space-y-1 rb-inner-card-sm">
                    <p className="text-slate-500 font-bold">Step totals</p>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Sum of fall heights:</span>
                      <span className="text-white font-bold">{fmt(params.steps.reduce((s, st) => s + st.h, 0), 3)} m</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">r_total (product):</span>
                      <span className="text-violet-400 font-bold">{fmt(results.rTotal, 4)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Auto tower height (+20%):</span>
                      <span className="text-sky-400 font-bold">{fmt(autoTowerHeight, 3)} m</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Weir Design ─────────────────────────────── */}
              {activeTab === 'weir' && (
                <div className="p-4 space-y-4 rb-card">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold flex items-center gap-1.5">
                    <Gauge className="w-3 h-3 text-amber-400" /> Weir Design (Sharp-Crested, Casey)
                  </p>
                  <S label="Weir length b" value={params.weirLength} min={1} max={20} step={0.1} unit="m"
                    icon={ArrowRight} color="#f59e0b" onChange={v => set('weirLength', v)}
                    info="Effective length of the weir crest. Increase to reduce loading rate." />
                  <S label="Weir height above floor p1" value={params.weirHeight} min={0.1} max={2.0} step={0.05} unit="m"
                    icon={ArrowDown} color="#8b5cf6" onChange={v => set('weirHeight', v)}
                    info="Height of weir crest above the base of the chamber (p1 in Casey formula)." />

                  {/* Weir results */}
                  <div className="space-y-2">
                    <div className={`flex items-center justify-between p-3 rounded-lg border ${results.vWeirLoading ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'}`}>
                      <div className="flex items-center gap-2">
                        {results.vWeirLoading ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-rose-400" />}
                        <span className="text-xs font-bold text-slate-300">Weir loading rate</span>
                      </div>
                      <span className={`text-sm font-bold tabular-nums ${results.vWeirLoading ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {fmt(results.weirLoadingRate, 2)} <span className="text-xs font-normal">L/s/m</span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rb-inner-card-sm">
                      <span className="text-xs text-slate-500">Head over weir h1</span>
                      <span className="text-sm font-bold text-amber-400 tabular-nums">{fmt(results.headOverWeir, 4)} m</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rb-inner-card-sm">
                      <span className="text-xs text-slate-500">Ce (discharge coeff.)</span>
                      <span className="text-sm font-bold text-slate-300 tabular-nums">
                        {fmt(0.602 + 0.075 * (results.headOverWeir / params.weirHeight), 4)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rb-inner-card-sm">
                      <span className="text-xs text-slate-500">Limit (standard)</span>
                      <span className="text-sm font-bold text-slate-500">36 L/s/m</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Tower & Ventilation ─────────────────────── */}
              {activeTab === 'tower' && (
                <div className="p-4 space-y-4 rb-card">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold flex items-center gap-1.5">
                    <Settings className="w-3 h-3 text-indigo-400" /> Tower Geometry & Ventilation
                  </p>
                  <S label="Tower diameter" value={params.towerDiameter} min={1} max={10} step={0.1} unit="m"
                    icon={Zap} color="#6366f1" onChange={v => set('towerDiameter', v)}
                    info="Internal diameter of the cascade tower. Affects tower volume for ventilation calc." />
                  <S label="Tower total height" value={params.towerHeight} min={1} max={20} step={0.001} unit="m"
                    icon={ArrowDown} color="#8b5cf6" onChange={v => set('towerHeight', v)}
                    info="Total height from base to top. Auto-calc = sum of falls × 1.2 (20% freeboard)." />

                  <button
                    onClick={() => set('towerHeight', autoTowerHeight)}
                    className="w-full text-xs font-medium px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-400 transition-colors"
                  >
                    Auto-set height = {fmt(autoTowerHeight, 3)} m (falls × 1.2)
                  </button>

                  <S label="Exhaust O₂ %" value={params.exhaustO2Pct} min={15} max={20.9} step={0.1} unit="%"
                    icon={Wind} color="#06b6d4" onChange={v => set('exhaustO2Pct', v)}
                    info="Expected O₂ in exhaust air from tower. Standard = 19% (2% O₂ utilisation from 21%)." />

                  {/* Ventilation results */}
                  <div className="space-y-2">
                    {[
                      { label: 'Tower volume', value: `${fmt(results.towerVolume, 2)} m³`, icon: Layers, color: '#6366f1' },
                      { label: 'O₂ inlet', value: '21.0 %', icon: Wind, color: '#94a3b8' },
                      { label: 'O₂ exhaust', value: `${fmt(params.exhaustO2Pct, 1)} %`, icon: Wind, color: '#06b6d4' },
                      { label: 'DO absorption rate', value: `${fmt(results.doAbsorptionRate, 3)} kg/h`, icon: Droplets, color: '#10b981' },
                      { label: 'Air requirement', value: `${fmt(results.airRequirement, 1)} Nm³/h`, icon: Wind, color: '#f59e0b' },
                    ].map(item => (
                      <div key={item.label} className="flex items-center justify-between p-3 rb-inner-card-sm">
                        <div className="flex items-center gap-2">
                          <item.icon className="w-3.5 h-3.5" style={{ color: item.color }} />
                          <span className="text-xs text-slate-500">{item.label}</span>
                        </div>
                        <span className="text-sm font-bold tabular-nums" style={{ color: item.color }}>{item.value}</span>
                      </div>
                    ))}
                    <div className={`flex items-center justify-between p-3 rounded-lg border ${results.vAirChanges ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'}`}>
                      <div className="flex items-center gap-2">
                        {results.vAirChanges ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-rose-400" />}
                        <span className="text-xs font-bold text-slate-300">Air changes per hour</span>
                      </div>
                      <span className={`text-sm font-bold tabular-nums ${results.vAirChanges ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {fmt(results.airChanges, 2)} /h
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── Right panel: Results & Charts ──────────────── */}
        <div className="space-y-4">
          {/* Process flow diagram */}
          <CascadeFlowDiagram params={params} results={results} />

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DOProfileChart results={results} />
            <DeficitRatioChart results={results} />
          </div>

          {/* Per-step results table */}
          <div className="overflow-hidden rb-card">
            <div className="flex items-center gap-2 p-4 border-b border-slate-800">
              <BarChart3 className="w-4 h-4 text-sky-400" />
              <span className="text-sm font-bold text-slate-200">Per-Step Results</span>
              <span className="ml-auto text-[10px] text-slate-500">Cs = {fmt(results.cs, 3)} mg/L at {fmt(params.temperature, 0)}°C</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-800 bg-rb-elevated">
                    {['Step', 'h (m)', 'r', 'Cu (mg/L)', 'Cd (mg/L)', 'DO Gain', '% Sat', 'Status'].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.steps.map((s, i) => {
                    const pass = results.vStepR[i] && results.vStepHeightMax[i] && results.vStepHeightMin[i];
                    return (
                      <tr key={i} className={`border-b border-slate-800/50 ${!pass ? 'bg-rose-500/5' : 'hover:bg-rb-elevated'} transition-colors`}>
                        <td className="px-3 py-2.5 font-bold text-slate-200">{ORDINALS[i]}</td>
                        <td className="px-3 py-2.5 font-mono text-slate-300">{fmt(s.h, 3)}</td>
                        <td className="px-3 py-2.5 font-mono text-violet-400">{fmt(s.r, 4)}</td>
                        <td className="px-3 py-2.5 font-mono text-amber-400">{fmt(s.cuUpstream, 3)}</td>
                        <td className="px-3 py-2.5 font-mono text-sky-400">{fmt(s.cdDownstream, 3)}</td>
                        <td className="px-3 py-2.5 font-mono text-emerald-400">+{fmt(s.doGain, 3)}</td>
                        <td className="px-3 py-2.5 text-slate-300">{fmt(s.pctSaturation, 1)}%</td>
                        <td className="px-3 py-2.5">
                          <ResultBadge pass={pass} label={pass ? 'Pass' : 'Fail'} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-rb-elevated border-t border-slate-700">
                    <td className="px-3 py-2.5 font-bold text-slate-300 text-[11px]">Totals</td>
                    <td className="px-3 py-2.5 font-bold text-slate-300 font-mono">
                      {fmt(params.steps.reduce((s, st) => s + st.h, 0), 3)} m
                    </td>
                    <td className="px-3 py-2.5 font-bold text-violet-400 font-mono">{fmt(results.rTotal, 4)}</td>
                    <td className="px-3 py-2.5 text-slate-500 text-[10px]">(inlet)</td>
                    <td className="px-3 py-2.5 font-bold text-sky-400 font-mono">{fmt(results.finalDO, 3)}</td>
                    <td className="px-3 py-2.5 font-bold text-emerald-400 font-mono">+{fmt(results.totalDOAbsorption, 3)}</td>
                    <td className="px-3 py-2.5 font-bold text-slate-300">{fmt(results.finalPctSat, 1)}%</td>
                    <td className="px-3 py-2.5">
                      <ResultBadge pass={allValid} label={allValid ? 'Pass' : 'Review'} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Summary panel */}
          <div className="p-4 rb-card">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-3 flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3 text-sky-400" /> Design Summary
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              {[
                { label: 'Final effluent DO', value: `${fmt(results.finalDO, 3)} mg/L`, sub: `${fmt(results.finalPctSat, 1)}% of Cs`, pass: results.vFinalDO, color: '#10b981' },
                { label: 'DO absorption', value: `${fmt(results.totalDOAbsorption, 3)} mg/L`, sub: `${fmt(results.doAbsorptionRate, 3)} kg/h`, pass: true, color: '#06b6d4' },
                { label: 'Weir loading', value: `${fmt(results.weirLoadingRate, 2)} L/s/m`, sub: results.vWeirLoading ? '≤ 36 ✓' : '> 36 ✗', pass: results.vWeirLoading, color: results.vWeirLoading ? '#10b981' : '#f43f5e' },
                { label: 'Head over weir', value: `${fmt(results.headOverWeir, 4)} m`, sub: 'h1 (iterated)', pass: true, color: '#f59e0b' },
                { label: 'Air requirement', value: `${fmt(results.airRequirement, 1)} Nm³/h`, sub: `${fmt(results.airChanges, 2)} changes/h`, pass: results.vAirChanges, color: results.vAirChanges ? '#10b981' : '#f43f5e' },
                { label: 'Tower volume', value: `${fmt(results.towerVolume, 2)} m³`, sub: `⌀${fmt(params.towerDiameter, 1)} × H${fmt(params.towerHeight, 2)} m`, pass: true, color: '#6366f1' },
              ].map(item => (
                <div key={item.label} className={`rounded-lg border p-3 ${!item.pass ? 'border-rose-500/30 bg-rose-500/5' : 'border-slate-800 bg-rb-elevated'}`}>
                  <p className="text-[10px] text-slate-500 mb-1">{item.label}</p>
                  <p className="font-bold tabular-nums" style={{ color: item.color }}>{item.value}</p>
                  <p className="text-[10px] text-slate-600 mt-0.5">{item.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Validation panel */}
          <div className="p-4 rb-card">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-3 flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" /> Design Validation
            </p>
            <div className="space-y-2">
              <ValidationRow
                pass={results.vWeirLoading}
                label="Weir loading ≤ 36 L/s/m"
                detail={`Actual: ${fmt(results.weirLoadingRate, 2)} L/s/m · Limit: 36 L/s/m (standard)`}
              />
              <ValidationRow
                pass={results.vFinalDO}
                label={`Final DO ≥ ${fmt(params.targetDO, 1)} mg/L`}
                detail={`Achieved: ${fmt(results.finalDO, 3)} mg/L (${fmt(results.finalPctSat, 1)}% of Cs = ${fmt(results.cs, 3)} mg/L)`}
              />
              <ValidationRow
                pass={results.vStepHeightMax.every(Boolean)}
                label="All step heights ≤ 3.0 m (structural)"
                detail={`Max: ${fmt(Math.max(...params.steps.map(s => s.h)), 3)} m · ${results.vStepHeightMax.every(Boolean) ? 'All within limit' : 'Steps exceed limit: ' + params.steps.map((s, i) => s.h > 3.0 ? ORDINALS[i] : null).filter(Boolean).join(', ')}`}
              />
              <ValidationRow
                pass={results.vStepHeightMin.every(Boolean)}
                label="All step heights ≥ 0.15 m (practical)"
                detail={`Min: ${fmt(Math.min(...params.steps.map(s => s.h)), 3)} m`}
              />
              <ValidationRow
                pass={results.vAirChanges}
                label="Air changes ≥ 5 per hour"
                detail={`Actual: ${fmt(results.airChanges, 2)}/h · Air required: ${fmt(results.airRequirement, 1)} Nm³/h`}
              />
              <ValidationRow
                pass={results.vStepR.every(Boolean)}
                label="All step deficit ratios r ≥ 1.05"
                detail={`Min r: ${fmt(Math.min(...results.steps.map(s => s.r)), 4)} · All steps: ${results.steps.map(s => fmt(s.r, 3)).join(', ')}`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ─── Science Accordion ──────────────────────────── */}
      <div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Microscope className="w-4 h-4 text-violet-400" /> Formula Derivations &amp; Science
        </p>
        <ScienceAccordion sections={scienceSections} />
      </div>

      {/* ─── Reference note ─────────────────────────────── */}
      <div className="flex items-start gap-3 p-4 rb-card">
        <Info className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
        <p className="text-[11px] text-slate-600 leading-relaxed">
          <span className="font-bold text-slate-500">Reference:</span> Twort A.C., Ratnayaka D.D. &amp; Brandt M.J. — <em>Water Supply</em>, 5th Edition, IWA Publishing, 1988, pp 390–391.
          Weir hydraulics after Casey (1992) — <em>Water &amp; Wastewater Engineering Hydraulics</em>.
          DO saturation polynomial from Benson &amp; Krause (1980). Air density 1.293 kg/Nm³ at STP.
          All calculations are pure client-side. No backend required.
        </p>
      </div>

      {/* ─── Report Modal ─────────────────────────────────── */}
      {showReport && (
        <PrintReportModal params={params} results={results} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}
